/**
 * Light or dark, and which one this browser is set to.
 *
 * The palette has been in `theme.css` since the sync was written — 32 dark
 * colours under `[data-theme="dark"]`, the same mechanism the prototype uses
 * — and **nothing ever set the attribute**, so a shipped theme was
 * unreachable. This is the switch.
 *
 * Three settings rather than two. *System* is the honest default: a desk that
 * is light on a machine set to dark is a desk fighting its owner, and a desk
 * that picks dark for somebody who never asked is the same mistake the other
 * way. Choosing light or dark pins it, and the choice survives a reload.
 */
export type ThemeChoice = 'light' | 'dark' | 'system'

const KEY = 'hi.theme'

/** What the machine is set to, when the choice is `system`. */
const systemIsDark = () =>
  typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches

export function readThemeChoice(): ThemeChoice {
  try {
    const saved = localStorage.getItem(KEY)
    if (saved === 'light' || saved === 'dark' || saved === 'system') return saved
  } catch {
    // A browser with storage blocked still gets a working desk, on system.
  }
  return 'system'
}

/**
 * Put it on `<html>`, and tell the browser too.
 *
 * `color-scheme` is not decoration: it is what makes the native scrollbar,
 * the `<select>` popup and a date input's own calendar come back dark. Set
 * the attribute without it and the desk goes dark around a white scrollbar
 * and a white dropdown, which looks broken rather than themed.
 */
export function applyTheme(choice: ThemeChoice): void {
  const dark = choice === 'dark' || (choice === 'system' && systemIsDark())
  const root = document.documentElement
  root.dataset.theme = dark ? 'dark' : 'light'
  root.style.colorScheme = dark ? 'dark' : 'light'
}

export function saveThemeChoice(choice: ThemeChoice): void {
  try {
    localStorage.setItem(KEY, choice)
  } catch {
    // Not fatal. It applies for this session and asks again next time.
  }
  applyTheme(choice)
}

/**
 * Follow the machine while the choice is `system`.
 *
 * Returns the unsubscribe. Without it, somebody on *System* who switches
 * their Mac to dark at sunset keeps a light desk until they reload — which
 * is exactly the case *System* exists to serve.
 */
export function watchSystemTheme(current: () => ThemeChoice): () => void {
  if (typeof matchMedia !== 'function') return () => {}
  const query = matchMedia('(prefers-color-scheme: dark)')
  const onChange = () => {
    if (current() === 'system') applyTheme('system')
  }
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}
